import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, MapPin, Star, Sparkles, Plane, UserPlus, Globe2, Users, CalendarHeart, Contact } from "lucide-react";
import { PushCard } from "@/components/settings/PushCard";
import { format, formatDistanceToNowStrict, isValid, parseISO } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { FollowBackInline } from "./FollowersStrip";
import { RequestButtons, useFriendRequests } from "./FriendRequests";

const first = (n) => n.split(" ")[0];
const day = (d) => (isValid(parseISO(d || "")) ? format(parseISO(d), "d MMM") : d);
const TYPES = {
  visited: { icon: Globe2, color: "#34D399", text: (a, d) => <><b>{first(a.name)}</b> has been to <b>{d.country}</b>{d.cities?.length ? ` (${d.cities.join(", ")})` : ""}</> },
  city: { icon: MapPin, color: "#39FF88", text: (a, d) => <><b>{first(a.name)}</b> visited <b>{d.city}</b>, {d.country}</> },
  wish: { icon: Star, color: "#F59E0B", text: (a, d) => <><b>{first(a.name)}</b> added <b>{d.country}</b> to their bucketlist</> },
  goal: { icon: Sparkles, color: "#FB7185", text: (a, d) => <><b>{first(a.name)}</b> added a dream: <b>{d.title}</b></> },
  invite: { icon: Plane, color: "#7DD3FC", text: (a, d) => <><b>{first(a.name)}</b> invited you to plan a trip to <b>{d.country}</b></> },
  follow: { icon: UserPlus, color: "#7DD3FC", text: (a) => <><b>{first(a.name)}</b> started following you</> },
  friend_request: { icon: UserPlus, color: "#7DD3FC", text: (a) => <><b>{first(a.name)}</b> sent you a friend request</> },
  joined: { icon: UserPlus, color: "#34D399", text: (a) => <><b>{first(a.name)}</b> made their own bucket list from your link</> },
  friend_accept: { icon: Users, color: "#34D399", text: (a) => <><b>{first(a.name)}</b> accepted your friend request</> },
  nearby: { icon: MapPin, color: "#38BDF8", text: (a, d) => <><b>{first(a.name)}</b> is in <b>{d.city}</b> — {d.country} is on your Want to go list</> },
  overlap_here: { icon: MapPin, color: "#38BDF8", text: (a, d) => <><b>{first(a.name)}</b> is in <b>{d.city}</b> while you're in {d.country}</> },
  overlap: { icon: CalendarHeart, color: "#F472B6", text: (a, d) => <>Trip overlap! <b>{first(a.name)}</b> is also going to <b>{d.country}</b> around {day(d.date)}</> },
  contacts_trip: { icon: Contact, color: "#FCD34D", text: (a, d) => <>You know people in <b>{d.country}</b>: {d.names}</> },
  pb_joined: { icon: UserPlus, color: "#34D399", text: (a, d) => <><b>{d.name || a.name}</b> from your phone book is on the bucketlist</> },
  contacts_here: { icon: Contact, color: "#FCD34D", text: (a, d) => <>You're in <b>{d.city}</b> — say hi to {d.names}</> },
};

const InlineRequest = ({ actor }) => {
  const { data } = useFriendRequests();
  if (!data?.incoming?.some((p) => p.user_id === actor.user_id)) return null;
  return <div className="mt-1.5"><RequestButtons p={actor} small /></div>;
};

const REL = { friend: "Friends", sent: "Request sent" };
const AddInline = ({ actor, i, relation }) => {
  const [state, setState] = useState(REL[relation] || "");
  const add = () => api.sendRequest(actor.user_id).then((r) => setState(r.relation === "friend" ? "Friends" : "Request sent"), (e) => setState(e?.response?.data?.detail || "Couldn't send"));
  if (state) return <p className="mt-1.5 text-[11px] text-emerald-300" data-testid={`notif-add-friend-state-${i}`}>{state}</p>;
  return <button onClick={add} className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-400/15 px-2.5 py-1 text-[11px] text-emerald-200 hover:bg-emerald-400/25 transition-colors" data-testid={`notif-add-friend-${i}`}><UserPlus size={11} /> Add friend</button>;
};

const Item = ({ n, i }) => {
  const T = TYPES[n.type] || TYPES.follow;
  return (
    <li className={`flex gap-3 px-4 py-3 ${n.unread ? "bg-white/[0.03]" : ""}`} data-testid={`notification-${i}`}>
      <span className="mt-0.5 w-7 h-7 shrink-0 rounded-full grid place-items-center" style={{ background: `${T.color}22`, color: T.color }}><T.icon size={14} /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-300 leading-snug [&_b]:text-slate-100 [&_b]:font-medium">{T.text(n.actor, n.data)}</p>
        <p className="font-mono text-[10px] text-slate-500 mt-1">{formatDistanceToNowStrict(parseISO(n.at), { addSuffix: true })}</p>
        {n.type === "follow" && <FollowBackInline actor={n.actor} />}
        {(n.type === "friend_request" || n.type === "joined") && <InlineRequest actor={n.actor} />}
        {n.type === "pb_joined" && <AddInline actor={n.actor} i={i} relation={n.data.relation} />}
        {n.type === "invite" && <a href="#travel" className="inline-block mt-1.5 text-[11px] text-sky-300 hover:text-sky-200" data-testid={`notif-open-invite-${i}`}>See invite in Want to go →</a>}
      </div>
      {n.unread && <span className="mt-2 w-2 h-2 rounded-full bg-rose-400 shrink-0" />}
    </li>
  );
};

// Tapping "Add friend" on a phone notification lands here with ?add_friend=<id>
const useAddFromPush = () => {
  useEffect(() => {
    const u = new URL(window.location.href);
    const id = u.searchParams.get("add_friend");
    if (!id) return;
    u.searchParams.delete("add_friend");
    window.history.replaceState(null, "", u.pathname + u.search + u.hash);
    api.sendRequest(id).then((r) => toast.success(r.relation === "friend" ? "You're friends now" : "Friend request sent"),
      (e) => toast.error(e?.response?.data?.detail || "Couldn't send the friend request"));
  }, []);
};

export const NotificationBell = () => {
  useAddFromPush();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: api.notifications, refetchInterval: 30000 });
  const unread = data?.unread || 0;

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && unread) api.notificationsSeen().catch(() => null);
    if (!o) qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Notifications" data-testid="notifications-button">
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-mono grid place-items-center" data-testid="notifications-badge">{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 bg-[#131A26] border-white/10" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-white/[0.06]">
          <p className="font-display text-lg">Friends' news</p>
        </div>
        <PushCard compact />
        {data?.items?.length ? (
          <ul className="max-h-[420px] overflow-y-auto divide-y divide-white/[0.05]">
            {data.items.map((n, i) => <Item key={`${n.type}-${n.at}-${i}`} n={n} i={i} />)}
          </ul>
        ) : (
          <p className="px-4 py-10 text-center text-sm text-slate-500" data-testid="notifications-empty">Nothing new yet. Follow friends to see their trips and dreams here.</p>
        )}
      </PopoverContent>
    </Popover>
  );
};
